"use client";

import { useActionState, useState } from "react";
import { useFormStatus } from "react-dom";
import { Link } from "@/i18n/navigation";
import { Button, Card, Input } from "@/components/ui";
import {
  requestMagicLinkAction,
  registerClientAction,
  type MagicLinkState,
  type RegisterState,
} from "@/lib/auth/actions";

export interface LoginLabels {
  emailLabel: string;
  emailPlaceholder: string;
  continue: string;
  sending: string;
  sentTitle: string;
  sentBody: string; // con {email}
  notFoundTitle: string;
  notFoundBody: string;
  demoBanner: string;
  openLink: string;
  backToEmail: string;
  registerToggle: string;
  loginToggle: string;
  registerTitle: string;
  registerSubtitle: string;
  nameLabel: string;
  namePlaceholder: string;
  dobLabel: string;
  dobHint: string;
  registerSubmit: string;
  /** Consentimiento con dos enlaces embebidos (Términos + Privacidad). */
  terms: {
    prefix: string;
    termsLink: string;
    connector: string;
    privacyLink: string;
    suffix: string;
  };
  errors: Record<string, string>;
}

function SubmitButton({ idle, busy }: { idle: string; busy: string }) {
  const { pending } = useFormStatus();
  return (
    <Button type="submit" block disabled={pending}>
      {pending ? busy : idle}
    </Button>
  );
}

/**
 * Formulario de login bilingüe. Dos modos:
 *  - "email": pide el correo → magic link (simulado). En dev muestra el enlace.
 *  - "register": crea una cuenta de cliente nueva (L0) y entra.
 * El locale y returnTo viajan como hidden para que los server actions redirijan
 * al lugar correcto sin depender del request.
 */
export function LoginForm({
  locale,
  returnTo,
  labels,
}: {
  locale: string;
  returnTo?: string;
  labels: LoginLabels;
}) {
  const [mode, setMode] = useState<"email" | "register">("email");

  const [magic, magicAction] = useActionState<MagicLinkState, FormData>(
    requestMagicLinkAction,
    { status: "idle" },
  );
  const [register, registerAction] = useActionState<RegisterState, FormData>(
    registerClientAction,
    { status: "idle" },
  );

  if (mode === "register") {
    return (
      <Card elevation="floating" className="mt-6">
        <h2 className="font-display text-lg text-ink">{labels.registerTitle}</h2>
        <p className="mt-1 text-sm text-ink-muted">{labels.registerSubtitle}</p>
        <form action={registerAction} className="mt-4 flex flex-col gap-3">
          <input type="hidden" name="locale" value={locale} />
          <Input
            name="name"
            label={labels.nameLabel}
            placeholder={labels.namePlaceholder}
            defaultValue={register.values?.name}
            autoComplete="name"
            required
          />
          <Input
            name="email"
            type="email"
            label={labels.emailLabel}
            placeholder={labels.emailPlaceholder}
            defaultValue={register.values?.email}
            autoComplete="email"
            required
            error={
              register.status === "error" &&
              register.reasonKey === "auth.register.email_taken"
                ? labels.errors[register.reasonKey]
                : undefined
            }
          />
          {/* Gate 18+: fecha de nacimiento. El servidor recalcula la edad
              (no confía en el cliente) y rechaza a los menores de 18. */}
          <Input
            name="dateOfBirth"
            type="date"
            label={labels.dobLabel}
            hint={labels.dobHint}
            defaultValue={register.values?.dateOfBirth}
            autoComplete="bday"
            required
            error={
              register.status === "error" &&
              (register.reasonKey === "auth.register.underage" ||
                register.reasonKey === "auth.register.dob_required")
                ? labels.errors[register.reasonKey]
                : undefined
            }
          />
          {/* Consentimiento explícito de Términos + Aviso de privacidad
              (in-app, requisito de tienda). Se revalida en el servidor. */}
          <label className="mt-1 flex items-start gap-2.5 text-sm text-ink-soft">
            <input
              type="checkbox"
              name="acceptTerms"
              required
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-sand-300 text-forest focus:ring-forest"
            />
            <span>
              {labels.terms.prefix}
              <Link
                href="/terminos"
                target="_blank"
                className="font-medium text-forest underline underline-offset-2 hover:text-forest-strong"
              >
                {labels.terms.termsLink}
              </Link>
              {labels.terms.connector}
              <Link
                href="/privacidad"
                target="_blank"
                className="font-medium text-forest underline underline-offset-2 hover:text-forest-strong"
              >
                {labels.terms.privacyLink}
              </Link>
              {labels.terms.suffix}
            </span>
          </label>
          {register.status === "error" &&
            register.reasonKey === "auth.register.terms_required" && (
              <p className="text-sm text-clay-strong">
                {labels.errors["auth.register.terms_required"]}
              </p>
            )}
          {register.status === "error" &&
            (register.reasonKey === "auth.register.invalid_name" ||
              register.reasonKey === "auth.register.invalid_email" ||
              register.reasonKey === "auth.register.rate_limited" ||
              register.reasonKey === "auth.register.blocked") && (
              <p className="text-sm text-clay-strong">
                {labels.errors[register.reasonKey]}
              </p>
            )}
          <SubmitButton idle={labels.registerSubmit} busy={labels.sending} />
        </form>
        <button
          type="button"
          onClick={() => setMode("email")}
          className="mt-4 text-sm font-medium text-forest hover:underline"
        >
          {labels.loginToggle}
        </button>
      </Card>
    );
  }

  return (
    <Card elevation="floating" className="mt-6">
      {magic.status === "sent" ? (
        <div>
          <h2 className="font-display text-lg text-ink">{labels.sentTitle}</h2>
          <p className="mt-1 text-sm text-ink-muted">
            {labels.sentBody.replace("{email}", magic.email ?? "")}
          </p>
          {magic.devLink && (
            <div className="mt-4 rounded-xl border border-dashed border-clay/50 bg-clay/5 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-clay">
                {labels.demoBanner}
              </p>
              <Link
                href={magic.devLink}
                className="mt-3 inline-flex h-10 items-center justify-center rounded-full bg-forest px-5 text-sm font-medium text-bone-50 transition-colors hover:bg-forest-strong"
              >
                {labels.openLink}
              </Link>
            </div>
          )}
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 text-sm font-medium text-forest hover:underline"
          >
            {labels.backToEmail}
          </button>
        </div>
      ) : (
        <>
          <form action={magicAction} className="flex flex-col gap-3">
            <input type="hidden" name="locale" value={locale} />
            {returnTo && <input type="hidden" name="returnTo" value={returnTo} />}
            <Input
              name="email"
              type="email"
              label={labels.emailLabel}
              placeholder={labels.emailPlaceholder}
              autoComplete="email"
              required
            />
            <SubmitButton idle={labels.continue} busy={labels.sending} />
          </form>

          {magic.status === "not_found" && (
            <div className="mt-4 rounded-xl border border-sand-300 bg-bone-50 p-4">
              <p className="text-sm font-medium text-ink">{labels.notFoundTitle}</p>
              <p className="mt-1 text-sm text-ink-muted">{labels.notFoundBody}</p>
            </div>
          )}

          <button
            type="button"
            onClick={() => setMode("register")}
            className="mt-4 text-sm font-medium text-forest hover:underline"
          >
            {labels.registerToggle}
          </button>
        </>
      )}
    </Card>
  );
}
